import {
  QUOTE_LINE_CUSTOMER_PART_ID_MAX_LENGTH,
  QUOTE_LINE_DESCRIPTION_MAX_LENGTH,
} from '@lib/models';
import {
  BASE_FIELDS_STEP_ID,
  DESCRIPTION_STEP_ID,
  MATERIAL_STEP_ID,
} from '@snap-types/models/configurator-snapshot/form/prerequisites';
import type {
  ConfigurationFieldSchema,
  ConfigurationStepSchema,
} from '@ui/features/configurator/types';

// Step Keys
const STEP_KEYS = {
  base_fields: 'base_fields',
  material: 'material',
  description: 'description',
} as const;

export const PREREQUISITE_STEP_KEYS = STEP_KEYS;

// Field IDs
const FIELD_IDS = {
  quantity: 'quantity',
  customer_part_id: 'customer_part_id',
  product_code: 'product_code',
  material: 'material',
  material_cost: 'material_cost',
  material_quantity_per: 'material_quantity_per',
  description: 'description',
} as const;

export const PREREQUISITE_FIELD_IDS = FIELD_IDS;

// Fields
const quantityField = {
  name: 'Quantity',
  field_id: FIELD_IDS.quantity,
  input_type: 'number',
  left_icon: 'hashtag',
  validations: [
    {
      validate: 'required',
      allow_zero: false,
    },
  ],
  dependencies: [],
  dependency_mode: null,
} satisfies ConfigurationFieldSchema;

const customerPartIdField = {
  name: 'Customer Part ID',
  field_id: FIELD_IDS.customer_part_id,
  input_type: 'text',
  validations: [
    {
      validate: 'required',
    },
    {
      validate: 'function',
      name: 'maxLength',
      max_length: QUOTE_LINE_CUSTOMER_PART_ID_MAX_LENGTH,
    },
  ],
  dependencies: [],
  dependency_mode: null,
} satisfies ConfigurationFieldSchema;

const productCodeField = {
  name: 'Product Code',
  field_id: FIELD_IDS.product_code,
  input_type: 'single_choice',
  field_level: 'operation',
  input_source: {
    source_type: 'static',
    data_key: 'product_code',
  },
  validations: [
    {
      validate: 'required',
    },
  ],
  dependencies: [],
  dependency_mode: null,
} satisfies ConfigurationFieldSchema;

const materialField = {
  name: 'Material',
  field_id: FIELD_IDS.material,
  input_type: 'material',
  input_source: {
    source_type: 'dynamic',
    source_key: 'material',
  },
  validations: [
    {
      validate: 'required',
    },
  ],
  dependencies: [],
  dependency_mode: null,
} satisfies ConfigurationFieldSchema;

const materialCostField = {
  name: 'Material Cost',
  field_id: FIELD_IDS.material_cost,
  input_type: 'number',
  left_icon: 'dollar',
  validations: [
    {
      validate: 'required',
      allow_zero: false,
    },
  ],
  dependencies: [
    {
      step_id: null,
      field_id: FIELD_IDS.material,
      dependency_type: 'simple',
    },
  ],
  dependency_mode: 'enable',
  clear_on_noninteractive: true,
} satisfies ConfigurationFieldSchema;

const materialQuantityPerField = {
  name: 'Weight (lbs/EA)',
  field_id: FIELD_IDS.material_quantity_per,
  input_type: 'number',
  validations: [
    {
      validate: 'required',
    },
  ],
  dependencies: [
    {
      step_id: null,
      field_id: FIELD_IDS.material,
      dependency_type: 'simple',
    },
  ],
  dependency_mode: 'enable',
  clear_on_noninteractive: true,
  calculator: 'VolumeCalculator',
} satisfies ConfigurationFieldSchema;

const descriptionField = {
  name: 'Description',
  field_id: FIELD_IDS.description,
  input_type: 'description',
  validations: [
    { validate: 'required' },
    {
      validate: 'function',
      name: 'maxLength',
      max_length: QUOTE_LINE_DESCRIPTION_MAX_LENGTH,
    },
  ],
  dependencies: [],
  dependency_mode: null,
} satisfies ConfigurationFieldSchema;

// Steps
export const defaultBaseFieldsStep = {
  id: BASE_FIELDS_STEP_ID,
  step_key: STEP_KEYS.base_fields,
  name: 'Base Fields',
  step_type: 'prerequisite',
  fields: [quantityField, productCodeField, customerPartIdField],
} satisfies ConfigurationStepSchema;

export const defaultMaterialStep = {
  id: MATERIAL_STEP_ID,
  step_key: STEP_KEYS.material,
  name: 'Material',
  step_type: 'prerequisite',
  fields: [materialField, materialCostField, materialQuantityPerField],
} satisfies ConfigurationStepSchema;

export const defaultDescriptionStep = {
  id: DESCRIPTION_STEP_ID,
  step_key: STEP_KEYS.description,
  name: 'Description',
  step_type: 'prerequisite',
  fields: [descriptionField],
} satisfies ConfigurationStepSchema;
